import { type ReactNode, useState } from 'react';

import { CartesianPlot, MARKER_HEADROOM, type PlotMargin } from './cartesian-plot';
import { CartesianTooltip } from './cartesian-tooltip';
import {
  buildConfig,
  computeValueMax,
  defaultBandSize,
  defaultMaxBarSize,
  fallbackColor,
  type ResolvedValueAxis,
  resolveValueAxis,
  toRows
} from './chart-series';
import { AXIS_HEADER_HEIGHT, ScrollFrame } from './scroll-frame';
import { SeriesMark } from './series-mark';
import type { CartesianChartProps, ChartSeries } from './types';
import { useCategoryWidth, useValueAxisWidth } from './use-measured-width';

/** 가로 막대 기본 뷰포트 높이(px) — 소비처가 viewport 를 안 넘겨도 되는 기본값. */
const DEFAULT_VIEWPORT = 320;
/** 가로 막대 **비스크롤** 여백 — 값축 마지막 tick 이 우측에서 안 잘리게 right 여유(스크롤 여백은 scroll-frame). */
const MARGIN_HORIZONTAL_STATIC: PlotMargin = { top: 8, right: 20, left: 4, bottom: 0 };

/** 값축 tick 의 **렌더 텍스트**(포맷터 적용) — 축 폭 실측 입력. */
function tickTexts(axis: ResolvedValueAxis): string[] {
  return axis.ticks.map((tick) => axis.tickFormatter?.(tick) ?? String(tick));
}

/**
 * CartesianChart — 통일 모델(`ChartModel`)을 받아 `CartesianPlot`(단일 recharts 렌더러)으로 조립하는 도메인
 * 무지 프리셋. bar/line/area · 누적(`stackId`)/그룹 · 이중축(`axis`) · 세로/가로(`orientation`) · 막대
 * 그라데이션을 단일 진입점으로 표현. 색·tick·툴팁·범례는 주입, sizing 은 기본값 내장.
 *
 * **값축 단일 해석**: 가시 series(범례 토글 반영) 의 raw max → `resolveValueAxis`(도메인·틱·단위·포맷) 를
 * 한 번 산출해 비스크롤·스크롤 전 경로에 균일 적용 → 토글로 series 가 바뀌면 단위·틱이 자동 재계산된다.
 *
 * **값축 폭 자동화**: 세로 막대 값축(좌·우) 폭은 고정값이 아니라 **tick 라벨 실측**(`useValueAxisWidth`) — 예전
 * 고정 44px 은 이중축이면 좌우로 88px 을 먹어 모바일 폭에서 plot 이 짓눌렸다. 좌우 margin 도 축이 흡수하지
 * 못하는 넘침분만 남긴다. 단위 라벨은 축 폭에 관여하지 않는다(`ValueAxisUnitLabel` 이 상단 여백 밴드 사용).
 *
 * **스크롤/반응형**: 가로 막대는 행 × 밴드높이가 `viewport` 를 넘으면 정적 판정으로 `ScrollFrame`(세로
 * 스크롤 + 값축 상단 고정). 세로 막대는 recharts `ResponsiveContainer` 로 컨테이너에 **반응형**(축·틱은
 * recharts 자체 지오메트리라 자동 정렬·유계 카테고리엔 스크롤 불필요) — 리사이즈에 결정적. 공개 진입점 단일.
 */
export function CartesianChart(props: CartesianChartProps) {
  const {
    model,
    orientation = 'vertical',
    xAxis,
    yAxis,
    tooltip,
    legend,
    legendToggle,
    maxBarSize,
    bandSize,
    viewport,
    valueDomain,
    referenceMarkers,
    highlightOnHover = true,
    className,
  } = props;
  const { xLabels } = model;
  const allSeries = model.series; // 범례는 전체 표기(끈 것도), 차트는 visible 만 렌더
  const horizontal = orientation === 'horizontal';
  const rows = xLabels.length;

  // 범례 토글로 끈 시리즈 = hidden. on 이고 숨긴 게 있으면 차트에서 필터 → 누적 stack·값축 단위 자동 재계산.
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const toggleSeries = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const series =
    legendToggle && hidden.size > 0 ? allSeries.filter((s) => !hidden.has(s.key)) : allSeries;

  // sizing 기본값 — 소비처가 안 넘기면 시리즈 구성/뷰포트로 결정.
  const resolvedViewport = viewport ?? DEFAULT_VIEWPORT;
  const resolvedBand = bandSize ?? defaultBandSize(series);
  const resolvedMaxBar = maxBarSize ?? defaultMaxBarSize(series);
  // 가로 막대 카테고리(행 라벨) 폭 실측 — direct·스크롤 경로 공유(긴 한글 라벨 recharts auto 잘림 방지). 세로면 미측정.
  const categoryWidth = useCategoryWidth(horizontal ? xLabels : []);

  // 색 config 는 **allSeries** 기준(안정 색인) — 범례도 allSeries 색인의 fallbackColor 를 쓰므로, 토글로
  // 가시 목록이 재색인돼도 막대 색(var(--color-{key}))과 범례 네모 색이 어긋나지 않는다. 값/행은 가시 series.
  const config = buildConfig(allSeries);
  const data = toRows(xLabels, series);
  const axis = resolveValueAxis(computeValueMax(data, series), yAxis?.left, valueDomain);

  // 이중축(우측) — 세로 막대 전용(일부 series 가 axis:'right' 일 때). 좌축처럼 우측 series raw max 로 resolve
  // (도메인·틱·포맷·단위). 우측은 보통 **라인**(보조 지표)이라 `computeValueMax`(막대 전용) 대신 평값 max.
  const rightSeries = series.filter((s) => s.axis === 'right');
  const rightAxisOpts = !horizontal && rightSeries.length > 0 ? yAxis?.right : undefined;
  const rightMax = data.reduce(
    (max, row) => rightSeries.reduce((m, s) => Math.max(m, Number(row[s.key]) || 0), max),
    0,
  );
  const resolvedRight = rightAxisOpts
    ? resolveValueAxis(rightMax, rightAxisOpts, undefined)
    : undefined;

  // 값축 폭 = tick 라벨 실측(고정 44px 이 좌·우 88px 을 먹어 좁은 화면 plot 이 짓눌리던 것). 가로 막대는 값축이
  // 상단 X축이라 미측정. hook 이라 조건부 호출 금지 → 미해당 경로엔 빈 배열(= min).
  const leftAxisWidth = useValueAxisWidth(horizontal ? [] : tickTexts(axis));
  const rightAxisWidth = useValueAxisWidth(resolvedRight ? tickTexts(resolvedRight) : []);
  const rightAxis = resolvedRight
    ? {
        ...resolvedRight,
        width: rightAxisOpts?.width ?? rightAxisWidth,
        hide: rightAxisOpts?.hide,
      }
    : undefined;

  const seriesByKey = new Map(series.map((s) => [s.key, s] as const));
  const tooltipEl =
    tooltip === false
      ? null
      : (tooltip ?? (
          <CartesianTooltip seriesByKey={seriesByKey} labelFormatter={xAxis?.tickFormatter} />
        ));

  // 범례 = recharts `<Legend>`(예약 높이 초과 시 멀티라인 깨짐) 대신 차트 **밖** HTML 블록 → flex-wrap 정상.
  // 스크롤 차트에선 스크롤 영역 밖(아래)이라 상시 노출(sticky 효과).
  const legendNode =
    legend === false
      ? null
      : (legend ?? (
          <ChartLegendHtml
            series={allSeries}
            toggle={legendToggle}
            hidden={hidden}
            onToggle={toggleSeries}
          />
        ));
  const withLegend = (body: ReactNode): ReactNode =>
    legendNode ? (
      <div className="flex w-full flex-col gap-3">
        {body}
        {legendNode}
      </div>
    ) : (
      body
    );

  // 가로 막대 + 행이 뷰포트(값축 헤더 제외) 초과 → 스크롤(정적 판정). 세로 막대는 반응형이라 스크롤 안 씀.
  if (horizontal && rows * resolvedBand + AXIS_HEADER_HEIGHT > resolvedViewport) {
    return withLegend(
      <ScrollFrame
        config={config}
        data={data}
        series={series}
        xLabels={xLabels}
        axis={axis}
        band={resolvedBand}
        viewport={resolvedViewport}
        categoryWidth={categoryWidth}
        maxBarSize={resolvedMaxBar}
        highlightOnHover={highlightOnHover}
        xAxis={xAxis}
        tooltipEl={tooltipEl}
      />,
    );
  }

  const directPlot = (
    <CartesianPlot
      orientation={orientation}
      config={config}
      data={data}
      series={series}
      axis={axis}
      margin={
        horizontal
          ? MARGIN_HORIZONTAL_STATIC
          : {
              // 상단 여백 = 단위 라벨 · 마커 스택 요구치 중 **큰 쪽**(합이 아님 — 마커 스택이 단위 라벨보다
              // 윗 행이라 그 오프셋에 단위 행이 이미 포함). 클리핑 방지.
              top: Math.max(
                axis.unit ? 28 : 8,
                referenceMarkers && referenceMarkers.length > 0 ? MARKER_HEADROOM : 0,
              ),
              // 좌우 여백 = 값축이 라벨 실폭으로 줄어든 만큼 최소치만. 좌측은 값축이 첫 카테고리 라벨의 넘침을
              // 흡수하므로 0(축을 숨기면 라벨 반쪽이 잘리니 복원), 우측은 우축이 있으면 그 축이 흡수.
              right: rightAxis ? 4 : 12,
              left: yAxis?.left?.hide ? 12 : 0,
              bottom: 0,
            }
      }
      categoryAxisSize={horizontal ? (xAxis?.width ?? categoryWidth) : undefined}
      valueAxisWidth={yAxis?.left?.width ?? (horizontal ? undefined : leftAxisWidth)}
      valueAxisHidden={yAxis?.left?.hide}
      showUnitLabel={!horizontal}
      maxBarSize={resolvedMaxBar}
      highlightOnHover={highlightOnHover}
      xAxis={xAxis}
      rightAxis={rightAxis}
      referenceMarkers={referenceMarkers}
      tooltipEl={tooltipEl}
    />
  );

  // 가로 막대 높이는 viewport 소유(등높이). 세로 막대: 단일 값축은 항상 ScrollFrame(sticky rail + CSS
  // min-width → 넓으면 채우고 좁으면 네이티브 가로 스크롤), 이중축만 인라인. 범례는 차트 밖(withLegend).
  return withLegend(
    horizontal ? (
      <div className="w-full" style={{ height: resolvedViewport }}>
        {directPlot}
      </div>
    ) : (
      <div className={`h-50 sm:h-65 lg:h-75 w-full ${className || ''}`}>{directPlot}</div>
    ),
  );
}

/**
 * 범례 — recharts `<Legend>` 대신 차트 밖 HTML(flex-wrap 멀티라인). 색은 시리즈 색 규칙과 동일
 * (`s.color ?? fallbackColor(i)` = ChartContainer config 가 `--color-{key}` 로 주입하는 값과 일치).
 * 아이콘 모양은 시리즈 타입을 따른다(`SeriesMark` — 툴팁과 공용). `toggle` 시 각 아이템이 버튼 → 클릭으로
 * on/off, off 는 아이콘이 비워지고(`hollow`) 라벨 dim.
 */
function ChartLegendHtml({
  series,
  toggle,
  hidden,
  onToggle,
}: {
  series: ChartSeries[];
  toggle?: boolean;
  hidden?: Set<string>;
  onToggle?: (key: string) => void;
}) {
  // 색은 **배열 index** 로 결정되므로(`fallbackColor(i)`) 정렬 전에 확정한다 — 정렬 후 index 로 뽑으면 색 미지정
  // 시리즈의 폴백 색이 플롯과 어긋난다. 정렬은 stable(ES2019+) 이라 같은 order 끼리는 배열 순서 유지.
  const items = series
    .map((s, i) => ({ s, color: s.color ?? fallbackColor(i) }))
    .sort((a, b) => (a.s.legendOrder ?? 0) - (b.s.legendOrder ?? 0));

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      {items.map(({ s, color }) => {
        const isHidden = Boolean(toggle && hidden?.has(s.key));
        const swatch = (
          <SeriesMark type={s.type} color={color} showDots={s.showDots} hollow={isHidden} />
        );
        return toggle ? (
          <button
            key={s.key}
            type="button"
            onClick={() => onToggle?.(s.key)}
            className={`flex items-center gap-1.5${isHidden ? ' text-muted-foreground/50' : ''}`}
          >
            {swatch}
            {s.label}
          </button>
        ) : (
          <span key={s.key} className="flex items-center gap-1.5">
            {swatch}
            {s.label}
          </span>
        );
      })}
    </div>
  );
}
